import { GeoJSONFeature, GeoJsonProperties } from './types';

import AbstractFeature from './AbstractFeature';
import AssetRegistry from '@Map/services/AssetRegistry';
import { Geometry } from '@Map/services/types';
import { deserialiseObjectValues } from '../utils';
import { t } from '@Translations/extraction';
import { SensorType } from '@innovyze/shared-utils';

export default class Feature implements AbstractFeature {
	private _featureData: GeoJSONFeature;
	private _id: string;

	constructor(featureData: GeoJSONFeature) {
		this._featureData = this._deserialiseProperties(featureData);
		this._id = featureData.properties?.id;
	}

	get id(): string {
		return this._id;
	}

	get properties(): GeoJsonProperties {
		const properties = {
			...this._featureData.properties,
			sourceId: this.sourceId,
			layerId: this.layerId,
			layerName: this.layerName,
			assetType: this.assetType,
			assetId: this.assetId,
			sensorId: this.assetId,
			type: this.assetCategory,
			displayType: this.displayType,
			geometry: this.geometry,
		} as GeoJsonProperties;

		const spatialDisplayName = this._getSpatialDisplayName(properties);
		if (spatialDisplayName) {
			properties.name = spatialDisplayName;
		}

		return properties;
	}

	get layerId(): string {
		return this._featureData.layer.id;
	}

	get sourceId(): string {
		return this._featureData.source ?? '';
	}

	get featureData(): GeoJSONFeature {
		return this._featureData;
	}

	get geometry(): Geometry {
		return this._featureData.geometry;
	}

	get layerZIndex(): number {
		return this._featureData.layer.metadata?.zIndex ?? 0;
	}

	get type(): Geometry['type'] {
		return this.geometry.type;
	}

	get layerName(): string {
		return t(this._featureData.layer.metadata.displayName, {
			context: 'Asset registry - plural item',
		});
	}

	get assetId(): string {
		if (this.id.match('#')) return this.id.split('#')[1];
		return this.id;
	}

	get assetType(): string {
		/**
		 * Originally, the following check was done to determine if feature is a sensor:
		 * 
		 * if (this._featureData.properties?.srcType) return this._featureData.properties.srcType;
		 * 
		 * But due to this bug https://jira.autodesk.com/browse/I360G-1047 we modified the logic to patch the problem.
		 * If the problem ever gets fixed on the BE, we probably want to remove this patch and bring back the original logic.
		 * 
		 * Be aware that currently there seems to be 2 different schemas being served to the FE and no one knows why.
		 * So we are handling both the old and the new schema.
		 */

		if (this._featureData.properties?.srcType) {
			const {srcType} = this._featureData.properties;
			if (Object.values(SensorType).includes(srcType)) return 'sensor';
			return srcType;
		}

		//
		if (this.id.match('#')) return this.id.split('#')[0];
		if (this._featureData.properties?.sequence) return 'incident';
		return 'unknown';
	}

	get assetCategory(): string {
		if (
			this.assetType === 'unknown' ||
			this.assetType === 'sensor' ||
			this.assetType === 'alert' ||
			this.assetType === 'incident' ||
			this.assetType === 'facility'
		)
			return this.assetType;
		if (this.assetType === 'amSpatial') return 'spatial';
		return 'asset';
	}

	get displayType(): string {
		if (this.assetType === 'sensor') {
			// get sensor type from id when in the format: sensor#34739.Status
			const sensorType = this.id.split(/\./).pop();
			if (sensorType) return sensorType;
		}
		return AssetRegistry.getDisplayName(this.assetType);
	}

	updateProperties(properties: GeoJsonProperties): void {
		this._featureData.properties = {
			...this._featureData.properties,
			...properties,
		};
	}

	asGeojsonFeature(sourceLayer?: string): GeoJSONFeature {
		return sourceLayer
			? { ...this.featureData, sourceLayer }
			: this.featureData;
	}

	private _deserialiseProperties(
		featureData: GeoJSONFeature,
	): GeoJSONFeature {
		return {
			...featureData,
			// geometry is a getter, cannot spread
			geometry: featureData.geometry,
			properties: deserialiseObjectValues(featureData.properties || {}),
		};
	}

	private _getSpatialDisplayName(
		properties: GeoJsonProperties,
	): string | undefined {
		if (this.assetCategory !== 'spatial') return undefined;

		const candidates = [
			properties.display_name,
			properties.alias,
			properties.name,
			properties.NAME,
			properties.Name,
			properties.assetId,
			properties.objectId,
			properties.OBJECTID,
			properties.id,
		];

		const firstLabel = candidates.find(candidate => {
			if (typeof candidate === 'number') return true;
			return typeof candidate === 'string' && candidate.trim().length > 0;
		});

		return firstLabel != null ? `${firstLabel}` : undefined;
	}
}
